/**
 * Enhanced Flood Types for Vietnam Flood Monitoring System
 * Comprehensive type definitions for all flood-related data
 */

import type { MultiPolygon, Polygon } from 'geojson';

import type { AlphaEarthIndex } from '@/features/map/types/map.types';
import { getFloodRiskHex } from '@/features/map/utils/floodRiskVisual';

// ============================================================================
// Enums & Constants
// ============================================================================

export type FloodCause =
  | 'heavy_rain'
  | 'high_tide'
  | 'blocked_drain'
  | 'dam_failure'
  | 'dam_break'
  | 'combined'
  | 'unknown';

export type AlertType =
  | 'flood_warning'
  | 'rainfall_alert'
  | 'tide_alert'
  | 'infrastructure_failure'
  | 'evacuation_order'
  | 'emergency_broadcast'
  | 'weather_warning'
  | 'landslide_warning';

export type AlertSeverity = 1 | 2 | 3 | 4 | 5;

export type TrafficStatus = 'blocked' | 'congested' | 'normal' | 'unknown';

export type FloodStatus = 'active' | 'monitoring' | 'resolved';

export type TrendDirection = 'rising' | 'stable' | 'falling';

export type RescueStationType = 'fire_station' | 'police' | 'hospital' | 'military' | 'volunteer';

export type EvacuationZoneStatus = 'available' | 'filling' | 'full';

export type ReportType =
  | 'situation_summary'
  | 'detailed_analysis'
  | 'damage_assessment'
  | 'forecast'
  | 'realtime'
  | 'evacuation';

export type ChartType = 'area' | 'bar' | 'line' | 'pie' | 'scatter' | 'heatmap';

export type ThemeMode = 'light' | 'dark' | 'system';

// ============================================================================
// Core Flood Point Types
// ============================================================================

export interface VulnerableGroups {
  elderly: number;
  children: number;
  disabled: number;
  pregnant: number;
}

export interface InfrastructureCount {
  hospitals: number;
  schools: number;
  emergencyShelters: number;
  fireStations: number;
  policeStations: number;
  evacuationCenters: number;
}

export interface BlockedRoad {
  id: string;
  name: string;
  lengthM: number;
  severity: 'blocked' | 'congested' | 'caution';
  alternateRoute: string;
  estimatedDelay: string;
}

export interface WaterLevelForecast {
  time: string;
  predictedDepthCm: number;
  confidence: number;
  trend: TrendDirection;
}

export interface FloodHistoryRecord {
  id: string;
  date: string;
  maxDepthCm: number;
  duration: string;
  cause: FloodCause;
  damageLevel: 'minor' | 'moderate' | 'severe';
}

export interface DamageAssessment {
  affectedHouses: number;
  damagedHouses: number;
  destroyedHouses: number;
  economicLossUSD: number;
  agriculturalAreaHa: number;
}

export interface EmergencyContact {
  name: string;
  role: string;
  phone: string;
  available: boolean;
}

export interface SafetyRecommendations {
  immediate: string[];
  shortTerm: string[];
  evacuationRoutes: string[];
  emergencyContacts: EmergencyContact[];
}

/** Land-cover breakdown for AOI popup; labels via i18n `flood.aoiInsight.landCover.*`. */
export type AoiLandCoverCategoryKey =
  | 'ricePaddy'
  | 'urbanBuilt'
  | 'forest'
  | 'perennialCrop'
  | 'surfaceWater';

export interface AoiLandCoverImpact {
  categoryKey: AoiLandCoverCategoryKey;
  areaHa: number;
}

/** Điểm hỗ trợ / an toàn gần nhất (ước tính trong pipeline hiển thị). */
export interface AoiNearestAssistance {
  label: string;
  distanceKm: number;
  etaMinutes?: number;
}

/** Hai biến thể RGB trong gallery (true color S2 vs embedding). */
export type AoiRemoteRgbProfile = 'sentinel2' | 'embedding';

/** Một chỉ số Alpha Earth trong slideshow Viễn thám. */
export interface AoiRemoteGeeFrame {
  index: AlphaEarthIndex;
  rgbProfile?: AoiRemoteRgbProfile;
  imageUrl?: string;
  caption?: string;
}

/**
 * Cửa sổ thời gian tham số (đồng bộ cách tính khoảng với tạo gallery mock).
 * Dùng cho thanh kéo mốc trong tab Viễn thám — ISO 8601, ranh theo ngày.
 */
export interface AoiRemoteTimeWindow {
  startIso: string;
  endIso: string;
}

/**
 * Tham chiếu / đối chiếu — từ `FloodJob.parameters` khi mở kết quả tác vụ.
 */
export interface AoiRemoteTimeWindows {
  baseline: AoiRemoteTimeWindow;
  crisis: AoiRemoteTimeWindow;
}

/** Slide ảnh viễn thám theo cửa sổ Baseline / Crisis (từ job SAR hoặc hydrate). */
export interface AoiRemoteSceneSlide {
  role: 'baseline' | 'crisis';
  periodLabel: string;
  /** Ngày gửi lên Alpha Earth (`date=`) — có khi nhiều mốc trong cùng role. */
  referenceDate?: string;
  imageUrl?: string;
  caption?: string;
  geeFrames?: AoiRemoteGeeFrame[];
}

/** Bối cảnh AOI bổ sung cho popup (generator có seed / pipeline phân tích). */
export interface AoiFloodContext {
  floodedAreaHa: number;
  /** Phần trăm so với đơn vị hành chính tham chiếu (0–100). */
  affectedAreaPercent: number;
  /** Nhãn đơn vị hành chính dùng cho % (ví dụ tên huyện). */
  administrativeUnitLabel: string;
  estimatedDepthMinM: number;
  estimatedDepthMaxM: number;
  landCoverImpacts: AoiLandCoverImpact[];
  baselinePeriodLabel: string;
  crisisSceneLabel: string;
  analysisModelLabel: string;
  previewBeforeUrl: string;
  previewAfterUrl: string;
  /** Mô tả bối cảnh bề mặt trước sự kiện (tóm tắt trung tính). */
  preEventSurfaceSummary: string;
  /** Ghi chú tham chiếu nguồn ước tính dân số trong AOI. */
  populationReferenceNote: string;
  nearestAssistance: AoiNearestAssistance;
  /** Ảnh xem trước theo khoảng ngày (ưu tiên hiển thị trong tab Viễn thám). */
  remoteSceneSlides?: AoiRemoteSceneSlide[];
  /** Đang hydrate URL tiles (Alpha Earth) cho gallery job. */
  remoteSceneGalleryLoading?: boolean;
  /** Vùng ngập ước lượng từ SAR trong AOI (overlay đỏ trên preview viễn thám). */
  sarFloodExtentGeometry?: Polygon | MultiPolygon | null;
  /**
   * Khoảng thời gian cấu hình (job SAR) — bật thanh kéo mốc + mô phỏng theo mốc.
   * Nguồn popup bản đồ không từ job: thường không có.
   */
  remoteTimeWindows?: AoiRemoteTimeWindows;
  /** Tỷ lệ che phủ thực vật (%) — dùng cho phân tích lớp phủ (land cover). */
  vegetationCoverPct?: number;
}

/**
 * Enhanced Flood Point - Complete flood point data model
 */
export interface EnhancedFloodPoint {
  // Identification
  id: string;
  name: string;
  position: [number, number]; // [lng, lat]

  // Flood parameters
  depthCm: number;
  maxDepthCm: number;
  areaKm2: number;
  volumeM3: number;

  // Time
  startTime: string;
  expectedEndTime: string;
  duration: string;
  lastUpdated: string;

  // Severity
  riskLevel: AlertSeverity;
  severityLabel: string;
  status: FloodStatus;

  // Cause
  cause: FloodCause;
  causeDescription: string;

  // Location
  province: string;
  district: string;
  ward: string;
  address: string;

  // Population
  population: number;
  households: number;
  vulnerableGroups: VulnerableGroups;

  // Infrastructure
  nearbyInfrastructure: InfrastructureCount;

  // Traffic
  blockedRoads: BlockedRoad[];
  trafficStatus: TrafficStatus;

  // Forecast
  forecast: WaterLevelForecast[];

  // History
  history: FloodHistoryRecord[];

  // Damage
  damageAssessment: DamageAssessment;

  // Safety
  safetyRecommendations: SafetyRecommendations;

  // Images
  photoUrl?: string;
  satelliteImageryUrl?: string;

  // Metadata
  confidence: number;
  dataSource: string;
  verified: boolean;

  /** Thống kê / viễn thám chi tiết theo AOI — tùy chọn. */
  aoiContext?: AoiFloodContext;

  /** Polygon AOI để xuất GeoJSON / GIS (ưu tiên polygon thật từ bản đồ hoặc SAR). */
  exportGeometry?: Polygon;
}

// ============================================================================
// Alert Types
// ============================================================================

export interface AlertLink {
  label: string;
  url: string;
  type: 'info' | 'action' | 'map';
}

export interface Alert {
  id: string;
  type: AlertType;
  severity: AlertSeverity;
  title: string;
  message: string;

  // Location
  position: [number, number];
  affectedArea: string;

  // Timing
  timestamp: string;
  expiresAt?: string;
  isActive: boolean;

  // Actions
  recommendedActions: string[];
  links: AlertLink[];

  // Status
  status: 'new' | 'acknowledged' | 'resolved';

  // Metadata
  source: string;
  verified: boolean;
}

export interface AlertFilters {
  severity?: AlertSeverity[];
  type?: AlertType[];
  status?: ('new' | 'acknowledged' | 'resolved')[];
  dateRange?: {
    start: string;
    end: string;
  };
}

// ============================================================================
// Routing Types
// ============================================================================

export interface FloodRiskPoint {
  position: [number, number];
  riskLevel: 1 | 2 | 3;
  description: string;
}

export interface RouteInstruction {
  step: number;
  instruction: string;
  distance: string;
  landmark?: string;
}

export interface AlternativeRoute {
  id: string;
  distanceKm: number;
  estimatedTime: string;
  floodRisk: 'none' | 'low' | 'medium' | 'high';
}

export interface SafeRoute {
  id: string;
  startPosition: [number, number];
  endPosition: [number, number];

  // Route details
  waypoints: [number, number][];
  distanceKm: number;
  estimatedTime: string;

  // Safety assessment
  safetyLevel: 'safe' | 'caution' | 'avoid';
  floodRiskPoints: FloodRiskPoint[];

  // Alternative routes
  alternatives: AlternativeRoute[];

  // Instructions
  instructions: RouteInstruction[];
}

export interface EvacuationZone {
  id: string;
  name: string;
  position: [number, number];
  capacity: number;
  currentOccupancy: number;
  status: EvacuationZoneStatus;
  amenities: string[];
  accessRoutes: string[];
}

export interface RescueStation {
  id: string;
  name: string;
  type: RescueStationType;
  position: [number, number];
  status: 'available' | 'busy' | 'unavailable';
  capacity: number;
  currentLoad: number;
  services: string[];
  contactPhone: string;
  distanceFromFlood?: number;
}

// ============================================================================
// Report Types
// ============================================================================

export interface CasualtyReport {
  deaths: number;
  injured: number;
  missing: number;
  evacuated: number;
  rescued: number;
}

export interface PropertyDamage {
  housesFlooded: number;
  housesDamaged: number;
  housesDestroyed: number;
  vehiclesAffected: number;
}

export interface EconomicLoss {
  totalUSD: number;
  agricultural: number;
  industrial: number;
  infrastructure: number;
  rescueCost: number;
}

export interface InfrastructureDamage {
  roadsKm: number;
  bridges: number;
  schools: number;
  hospitals: number;
}

export interface ReportSummary {
  totalFloodPoints: number;
  totalAffectedPopulation: number;
  totalAffectedArea: number;
  severityDistribution: Record<number, number>;
  trend: 'increasing' | 'stable' | 'decreasing';
}

export interface ReportStatistics {
  averageDepth: number;
  maxDepth: number;
  totalDuration: string;
  rainfallTotal: number;
  peakWaterLevel: number;
}

export interface AffectedAreaReport {
  name: string;
  population: number;
  area: number;
  avgDepth: number;
  maxDepth: number;
  status: 'flooded' | 'receding' | 'recovered';
}

export interface ResponseAction {
  time: string;
  action: string;
  responsibleParty: string;
  outcome: string;
}

export interface ReportAttachment {
  name: string;
  type: 'pdf' | 'excel' | 'image' | 'map';
  url: string;
  sizeKB: number;
}

export interface DamageReport {
  casualties: CasualtyReport;
  property: PropertyDamage;
  economic: EconomicLoss;
  infrastructure: InfrastructureDamage;
}

export interface FloodReport {
  id: string;
  type: ReportType;
  title: string;

  // Period
  period: {
    startDate: string;
    endDate: string;
  };

  // Content
  summary: ReportSummary;
  statistics: ReportStatistics;
  affectedAreas: AffectedAreaReport[];
  damageAssessment: DamageReport;
  responseActions: ResponseAction[];

  // Attachments
  attachments: ReportAttachment[];

  // Metadata
  generatedAt: string;
  generatedBy: string;
  approvedBy?: string;
}

export interface ReportTemplate {
  id: string;
  name: string;
  type: ReportType;
  description: string;
  sections: string[];
}

// ============================================================================
// Analytics Types
// ============================================================================

export interface TimeRange {
  start: string;
  end: string;
}

export interface HistoricalAnalysis {
  year: number;
  month?: number;
  totalEvents: number;
  totalAffectedPopulation: number;
  totalAffectedArea: number;
  severityDistribution: Record<number, number>;
  monthlyDistribution: Record<string, number>;
  peakWaterLevel: number;
  totalRainfall: number;
  economicLoss: number;
  events: {
    date: string;
    location: string;
    severity: AlertSeverity;
    maxDepth: number;
  }[];
}

export interface ComparisonResult {
  eventIds: string[];
  metrics: {
    name: string;
    values: Record<string, number>;
  }[];
  summary: {
    mostSevere: string;
    longestDuration: string;
    highestEconomicLoss: string;
  };
}

// ============================================================================
// Dashboard Types
// ============================================================================

export interface DashboardStats {
  alertCount: number;
  sensorsOnline: number;
  floodZones: number;
  areaMonitored: number;
  peakLevel: number;
  rainfall24h: number;
  affectedPopulation: number;
  trend: 'increasing' | 'stable' | 'decreasing';
}

export interface WeatherSnapshot {
  year: number;
  hour: number;
  tempC: number;
  windKmh: number;
  rainfall24hMm: number;
  humidityPct: number;
  trendTempC: number;
  trendRainMm: number;
  trendHumidityPct: number;
}

export interface AffectedArea {
  name: string;
  depthM: number;
  pop: number;
  status: 'severe' | 'warning';
}

// ============================================================================
// Utility Types
// ============================================================================

/** Nhãn tiếng Việt mặc định — đồng bộ với `map.riskLevels` / chú giải điểm ngập. */
export const SEVERITY_LABELS: Record<AlertSeverity, string> = {
  1: 'Thấp',
  2: 'Trung bình',
  3: 'Cao',
  4: 'Rất cao',
  5: 'Khẩn cấp',
};

export const SEVERITY_COLORS: Record<AlertSeverity, string> = {
  1: getFloodRiskHex(1),
  2: getFloodRiskHex(2),
  3: getFloodRiskHex(3),
  4: getFloodRiskHex(4),
  5: getFloodRiskHex(5),
};

export const CAUSE_LABELS: Record<FloodCause, string> = {
  heavy_rain: 'Mưa lớn kéo dài',
  high_tide: 'Triều cường dâng cao',
  blocked_drain: 'Cống thoát nước bị tắc',
  dam_failure: 'Hỏng đập thủy điện',
  dam_break: 'Vỡ đập',
  combined: 'Kết hợp nhiều nguyên nhân',
  unknown: 'Nguyên nhân chưa xác định',
};

export const ALERT_TYPE_LABELS: Record<AlertType, string> = {
  flood_warning: 'Cảnh báo ngập',
  rainfall_alert: 'Cảnh báo mưa lớn',
  tide_alert: 'Cảnh báo triều cường',
  infrastructure_failure: 'Sự cố hạ tầng',
  evacuation_order: 'Lệnh sơ tán',
  emergency_broadcast: 'Thông báo khẩn',
  weather_warning: 'Cảnh báo thời tiết',
  landslide_warning: 'Cảnh báo sạt lở',
};
